import * as API from '@/services/api.js'
import { toggleLoader } from '@/hooks/useDashboardLoader.jsx'
import { toast } from 'sonner'
import { AnalyticsService } from '@/services/analytics.js'
import { handleError } from '@/services/errorHandler.js'
import { useAuthContext } from '@/context-state/AuthContext.jsx'

export const useSpaceMemberForm = (spaceId) => {

    const { getSpaceUsers, setSpaceUsers } = useAuthContext()
    const spaceUsers = getSpaceUsers(spaceId)

    const fetchSpaceUsers = async () => {
        try {
            const { data } = await API.getSpaceUsers(spaceId)
            setSpaceUsers(spaceId, data)
        } catch (e) {
            console.error(e)
            toast.error('Failed to fetch space users')
        }
    }

    const submitMemberForm = async (memberData, selectedMember, setIsMemberDialogOpen) => {
        try {
            toggleLoader(true)
            setIsMemberDialogOpen(false)

            if (selectedMember) {
                // Update existing member
                const newSpaceUsers = spaceUsers.map(user => {
                    if (user.user === selectedMember.user) {
                        return { ...user, role: memberData.role }
                    }
                    return user
                })

                await API.updateSpace(spaceId, { users: newSpaceUsers })
                await fetchSpaceUsers()
                toast.success(`${memberData.name || memberData.email} role updated to ${memberData.role}`)

                AnalyticsService.track('member-role-updated-in-space', {
                    spaceId,
                    memberEmail: memberData.email,
                    role: memberData.role
                })

                return
            }

            // Add new member
            await API.assignUserToSpace(spaceId, {
                email: memberData.email,
                role: memberData.role,
                urlToAcceptInvitation: `${window.location.origin}/auth/accept-invitation`,
                baseAppUrl: window.location.origin
            })

            // Fetch updated space users
            await fetchSpaceUsers()
            toast.success(`Invitation sent to ${memberData.email}`)

            AnalyticsService.track('member-invited-to-space', {
                spaceId,
                memberEmail: memberData.email,
                role: memberData.role
            })

        } catch (error) {
            console.error(error)
            handleError(error)
            toast.error(error.message)
        } finally {
            toggleLoader(false)
        }
    }

    const confirmDeleteMember = async (memberToDelete, setMemberToDelete) => {
        try {
            if (!memberToDelete) {
                return
            }
            toggleLoader(true)
            setMemberToDelete(null)

            const userData = {
                user: memberToDelete.user,
                email: memberToDelete.email
            }

            await API.removeUserFromSpace(spaceId, userData)

            // Refresh the members list
            await fetchSpaceUsers()
            toast.success(`${memberToDelete.name || memberToDelete.email} has been removed`)

            AnalyticsService.track('member-removed-from-space', { spaceId, memberEmail: memberToDelete.email })

        } catch (error) {
            console.error(error)
            handleError(error)
            toast.error(error.message)
        } finally {
            toggleLoader(false)
            setMemberToDelete(null)
        }
    }

    return {
        submitMemberForm,
        confirmDeleteMember
    }
}